import React from "react";
import { Link } from "react-router-dom";

const IntelligentAutomationLayout = ({ service }) => {
    return (
        <div className="bg-white text-gray-900">

            {/* ================= HERO ================= */}
            <section
                className="relative min-h-[85vh] flex items-center"
                style={{
                    backgroundImage:
                        "url(/images/automation/hero.png)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-indigo-900/70" />
                <div className="relative w-full px-6 md:px-16 lg:px-24 py-24 text-white text-left">
                    <span className="text-sm uppercase tracking-wide text-orange-400">
                        Services
                    </span>
                    <h1 className="text-5xl md:text-6xl font-bold mt-3 mb-6">
                        Intelligent Automation
                    </h1>
                    <p className="text-xl max-w-3xl text-indigo-100 mb-8">
                        Automate and transform how work gets done with AI-powered,
                        low-code, and intelligent automation solutions.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-block bg-orange-500 hover:bg-orange-600 px-8 py-4 rounded-lg font-semibold"
                    >
                        Consult Our Experts
                    </Link>
                </div>
            </section>

            {/* ================= FEATURED INSIGHT ================= */}
            <section className="border-b">
                <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center gap-6">
                    <img
                        src="/images/automation/uipath.png"
                        alt="UiPath"
                        className="h-10"
                    />
                    <p className="text-sm text-gray-600 max-w-4xl">
                        <strong>Featured Insight:</strong> TechSPR recognized as a
                        UiPath Agentic Automation Fast Track Partner helping enterprises
                        accelerate intelligent automation adoption.
                    </p>
                </div>
            </section>

            {/* ================= OVERVIEW ================= */}
            <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
                <div>
                    <h2 className="text-4xl font-bold mb-6">
                        Intelligent Automation for the Enterprise
                    </h2>
                    <p className="text-gray-600 mb-6">
                        Intelligent automation helps businesses enhance customer experience,
                        improve employee productivity, and achieve faster ROI by combining
                        AI, process intelligence, and automation platforms.
                    </p>
                    <ul className="space-y-3 text-gray-700">
                        <li>✔ Process & task automation</li>
                        <li>✔ AI-driven decision intelligence</li>
                        <li>✔ Low-code application development</li>
                        <li>✔ Enterprise-scale orchestration</li>
                    </ul>
                </div>
                <img
                    src="/images/automation/ai.png"
                    alt="Automation overview"
                    className="rounded-xl"
                />
            </section>

            {/* ================= PROCESS INTELLIGENCE ================= */}
            <section className="bg-gray-50">
                <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12">
                    <div>
                        <h2 className="text-3xl font-bold mb-4">
                            Process Intelligence
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Discover, analyze, and optimize business processes using
                            process mining and task mining to drive operational excellence.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-block text-orange-600 font-semibold"
                        >
                            Learn more about our service offerings →
                        </Link>
                    </div>
                    <div className="bg-white p-8 rounded-xl shadow">
                        <h4 className="font-semibold mb-3">Our Offerings</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li>• Process discovery & mining</li>
                            <li>• Task mining & insights</li>
                            <li>• Automation opportunity assessment</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* ================= VIDEO STRIP ================= */}
            <section className="bg-blue-600 text-white">
                <div className="max-w-7xl mx-auto px-6 py-16 text-center">
                    <h2 className="text-3xl font-bold mb-4">
                        Leveraging AI and Low-Code to Build Enterprise Applications
                    </h2>
                    <p className="max-w-3xl mx-auto text-blue-100 mb-6">
                        Learn how enterprises are accelerating digital transformation using
                        AI-powered automation and low-code platforms.
                    </p>
                    <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold">
                        Watch the Video
                    </button>
                </div>
            </section>

            {/* ================= FRAMEWORK ================= */}
            <section className="relative bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-6 py-20">
                    <h2 className="text-4xl font-bold mb-12">
                        TechSPR Intelligent Business Automation Framework
                    </h2>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            { title: "Discover", desc: "Process & task mining" },
                            { title: "Digitize", desc: "OCR, IDP, low-code apps" },
                            { title: "Orchestrate", desc: "APIs, workflows" },
                            { title: "Automate", desc: "RPA, AI chatbots" },
                        ].map((item) => (
                            <div
                                key={item.title}
                                className="bg-white text-gray-900 p-6 rounded-xl"
                            >
                                <h4 className="font-semibold mb-2">{item.title}</h4>
                                <p className="text-sm text-gray-600">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= CLIENT SUCCESS ================= */}
            <section className="bg-indigo-900 text-white">
                <div className="max-w-7xl mx-auto px-6 py-20">
                    <h2 className="text-3xl font-bold mb-10">Client Success</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            "Operational excellence through automation",
                            "Loan processing accelerated with IA",
                            "Digital transformation at scale",
                        ].map((item) => (
                            <div key={item} className="bg-indigo-800 p-6 rounded-xl">
                                <p className="font-semibold">{item}</p>
                                <span className="text-sm text-indigo-200">
                                    Read the story →
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= DIFFERENTIATORS ================= */}
            <section className="bg-gray-50">
                <div className="max-w-7xl mx-auto px-6 py-20">
                    <h2 className="text-3xl font-bold mb-10">
                        Key Differentiators
                    </h2>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            "AI-first approach",
                            "Deep domain expertise",
                            "Strong advisory capabilities",
                            "Ecosystem partnerships",
                        ].map((item) => (
                            <div key={item} className="bg-white p-6 rounded-xl shadow">
                                <p className="font-semibold">{item}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= PARTNERS ================= */}
            <section className="border-t bg-white">
                <div className="max-w-7xl mx-auto px-6 py-16">

                    <h2 className="text-2xl font-bold mb-8">
                        Our Key Partnerships
                    </h2>

                    <div className="flex flex-wrap gap-10 items-center">
                        {[
                            { name: "AWS", logo: "aws" },
                            { name: "Microsoft", logo: "microsoft" },
                            { name: "Appian", logo: "appian" },
                            { name: "OutSystems", logo: "outsystems" },
                            { name: "UiPath", logo: "uipath" }
                        ].map((partner) => (
                            <div
                                key={partner.name}
                                className="flex items-center justify-center"
                            >
                                <img
                                    src={`/images/partners/${partner.logo}.png`}
                                    alt={partner.name}
                                    className="h-10 w-auto object-contain opacity-80 hover:opacity-100 transition"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>

                </div>
            </section>


            {/* ================= CTA ================= */}
            <section className="bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-6 py-20 text-center">
                    <h2 className="text-4xl font-bold mb-4">
                        Start Your Intelligent Automation Journey
                    </h2>
                    <p className="text-gray-300 mb-8">
                        Let’s build scalable, intelligent automation solutions together.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-block bg-orange-500 px-8 py-4 rounded-lg font-semibold"
                    >
                        Contact Us
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default IntelligentAutomationLayout;
